import { ethers } from 'ethers';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api.js';

const MOONPAY_API_KEY = 'pk_live_eFVxBJ8DUUWPEPAKQhQxGKRZJQQDVZc'; // BitSport MoonPay public key
// BNB Chain (BSC) Contract Addresses
const PRESALE_WALLET_ADDRESS = '0x3aDCCc8BfaC5DD5B036859C3258ed975534D239F';
const PRESALE_CONTRACT_ADDRESS = '0x3aDCCc8BfaC5DD5B036859C3258ed975534D239F'; // Using wallet address as contract
const USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955'; // BSC USDT
const USDC_ADDRESS = '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d'; // BSC USDC

const BSC_CHAIN_ID = '0x38'; // BSC Mainnet

export const SUPPORTED_TOKENS = {
  USDT: {
    address: USDT_ADDRESS,
    decimals: 18,
    symbol: 'USDT',
    name: 'Tether USD'
  },
  USDC: {
    address: USDC_ADDRESS,
    decimals: 18,
    symbol: 'USDC',
    name: 'USD Coin'
  }
};

export const switchToBSCNetwork = async () => {
  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BSC_CHAIN_ID }],
    });
    return true;
  } catch (error) {
    if (error.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: BSC_CHAIN_ID,
            chainName: 'BNB Smart Chain',
            nativeCurrency: {
              name: 'BNB',
              symbol: 'BNB',
              decimals: 18
            },
            rpcUrls: ['https://bsc-dataseed.binance.org/'],
            blockExplorerUrls: ['https://bscscan.com/']
          }]
        });
        return true;
      } catch (addError) {
        console.error('Error adding BSC network:', addError);
        return false;
      }
    }
    console.error('Error switching to BSC network:', error);
    return false;
  }
};

const recordPresaleContribution = async (data) => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.presale.transaction}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to record transaction');
    }

    return await response.json();
  } catch (error) {
    console.error('Error recording transaction:', error);
    throw error;
  }
};

export const connectWallet = async () => {
  try {
    const { ethereum } = window;
    if (!ethereum) {
      throw new Error('Please install MetaMask to participate in the presale');
    }

    // Request account access
    const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts found. Please connect your wallet.');
    }

    return accounts[0];
  } catch (error) {
    console.error('Error connecting wallet:', error);
    throw error;
  }
};

export const handlePresaleTransaction = async (amount, currency) => {
  try {
    const { ethereum } = window;
    if (!ethereum) {
      throw new Error('Please install MetaMask to participate in the presale');
    }

    // Validate amount
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount < 2) {
      throw new Error('Minimum purchase amount is 2 USDT/USDC');
    }
    if (numAmount > 10000) {
      throw new Error('Maximum purchase amount is 10,000 USDT/USDC');
    }

    // Switch to BSC network first
    const switched = await switchToBSCNetwork();
    if (!switched) throw new Error('Please switch to BNB Smart Chain network');

    const userAddress = await connectWallet();

    // Initialize provider and signer
    const provider = new ethers.BrowserProvider(ethereum);
    const signer = await provider.getSigner();

    const signerAddress = await signer.getAddress();
    if (signerAddress.toLowerCase() !== userAddress.toLowerCase()) {
      throw new Error('Wallet address mismatch');
    }

    const tokenContract = new ethers.Contract(
      SUPPORTED_TOKENS[currency].address,
      ['function approve(address spender, uint256 amount) returns (bool)'],
      signer
    );

    const amountInWei = ethers.parseUnits(amount.toString(), SUPPORTED_TOKENS[currency].decimals);

    // First approve the presale wallet to spend tokens
    console.log('Approving token spend...');
    const approveTx = await tokenContract.approve(PRESALE_CONTRACT_ADDRESS, amountInWei, {
      gasLimit: 100000 // Add gas limit for BSC
    });
    const approveReceipt = await approveTx.wait();
    console.log('Approval confirmed:', approveReceipt.hash);

    const tokenTransferContract = new ethers.Contract(
      SUPPORTED_TOKENS[currency].address,
      ['function transfer(address to, uint256 amount) returns (bool)'],
      signer
    );

    // Transfer tokens to presale wallet
    console.log('Transferring tokens...');
    const tx = await tokenTransferContract.transfer(PRESALE_CONTRACT_ADDRESS, amountInWei, {
      gasLimit: 100000 // Add gas limit for BSC
    });
    const receipt = await tx.wait();
    console.log('Transfer confirmed:', receipt.hash);

    try {
      await recordPresaleContribution({
        walletAddress: userAddress,
        amount: Number(amount),
        token: currency,
        transactionHash: tx.hash
      });
    } catch (backendError) {
      console.error('Failed to record contribution in backend:', backendError);
    }
    
    return { success: true, hash: tx.hash, walletAddress: userAddress };
  } catch (error) {
    console.error('MetaMask transaction failed:', error);
    throw error;
  }
};

export const handleMoonPayTransaction = async (amount, currency) => {
  try {
    if (!amount) {
      throw new Error('Please enter an amount');
    }
    
    // Check amount limits
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount < 2) {
      throw new Error('Minimum purchase amount is 2 USDT/USDC');
    }
    if (numAmount > 10000) {
      throw new Error('Maximum purchase amount is 10,000 USDT/USDC');
    }

    const baseUrl = 'https://buy.moonpay.com';
    const params = new URLSearchParams({
      apiKey: MOONPAY_API_KEY,
      currencyCode: currency.toLowerCase(),
      walletAddress: PRESALE_CONTRACT_ADDRESS,
      baseCurrencyAmount: amount,
      redirectURL: window.location.origin + '/presale',
      showWalletAddressForm: false,
      colorCode: '#3498db'
    });

    return { moonPayUrl: `${baseUrl}?${params.toString()}` };
  } catch (error) {
    throw new Error(error.message || 'Failed to initialize MoonPay transaction');
  }
};